import { useCallback, useEffect } from 'react';
import { Language, StatusOutput } from '@/api/types';
import { useSearchParams } from 'react-router-dom';
import { useGetStatus } from '@/api/services';
import * as Sentry from '@sentry/react';

import { useSessionStorage, SessionStorageKeys } from '@/utils/storage';
import {
  DEFAULT_LANGUAGE,
  useInterviewLanguage,
} from '@/hooks/useAppLanguage.ts';
import { clearResidencyAnswer } from '@/hooks/useResidency';

const resolveLocalizedData = (language: Language, data: StatusOutput | undefined) => {
  if (!data) {
    return { introVideoSrc: '', questionsTotal: 0 };
  }

  const fallbackData = data.intros.find((intro) => intro.language === 'english');
  const resolvedIntro = data.intros.find((intro) => intro.language === language) ?? fallbackData;

  return {
    introVideoSrc: resolvedIntro?.url ?? '',
    questionsTotal: resolvedIntro?.questions_total ?? 0,
  };
}

export const useQuestionsAnswers = () => {
  const [language, setLanguage] = useInterviewLanguage();
  const [currentQuestionNumber, setCurrentQuestionNumber] =
    useSessionStorage<number>(SessionStorageKeys.CURRENT_QUESTION_NUMBER, 0);
  const [invitationKey, _setInvitationKey] = useSessionStorage<string>(
    SessionStorageKeys.INVITATION_KEY
  );

  const [params] = useSearchParams();
  const paramsInvitationKey = params.get(SessionStorageKeys.INVITATION_KEY);

  const setInvitationKey = useCallback(
    (invitationKey) => {
      _setInvitationKey(invitationKey);
      setCurrentQuestionNumber(0);
      // A new invitation is a new candidate — the previous candidate's
      // residency declaration must not carry over on a shared phone.
      clearResidencyAnswer();
    },
    [_setInvitationKey, setCurrentQuestionNumber]
  );

  const { data, isError, error, isFetching, isLoading } =
    useGetStatus(invitationKey, language);

  useEffect(() => {
    if (paramsInvitationKey && paramsInvitationKey !== invitationKey) {
      setInvitationKey(paramsInvitationKey);
      // A new invitation is a new interview, so a language chosen for a previous
      // one must not carry over. The apply flow's language lives under a
      // different key and is never read here, so nothing it did can leak in.
      setLanguage(DEFAULT_LANGUAGE);
    }
  }, [invitationKey, paramsInvitationKey, setInvitationKey, setLanguage]);

  useEffect(() => {
    if (invitationKey) {
      Sentry.setUser({ id: invitationKey });
    }
  }, [invitationKey]);

  const { questionsTotal, introVideoSrc } = resolveLocalizedData(language, data);

  return {
    invitationKey,
    remoteStatus: data,
    localeData: {
      introVideoSrc,
      questionsTotal,
      estimatedTime: questionsTotal * 2,
    },
    language,
    currentQuestionNumber: currentQuestionNumber,
    langContext: {
      language,
      intro: introVideoSrc,
      questionsTotal: questionsTotal,
    },
    isLoading: isLoading && isFetching, // isFetching is used considering that we are using conditional fetch
    isError,
    error,
    setLanguage,
    setCurrentQuestionNumber,
    setInvitationKey,
  };
};
